import { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, Play, X } from "lucide-react";
import { cn } from "@/lib/utils";

export interface GalleryItem {
  src: string;
  alt?: string;
  type?: "image" | "video";
  caption?: string;
  poster?: string;
}

interface ImageGalleryProps {
  items: GalleryItem[];
  height?: number;
  className?: string;
}

export function ImageGallery({
  items,
  height = 440,
  className
}: ImageGalleryProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = "";
  };

  const navigateLightbox = (direction: "prev" | "next") => {
    if (direction === "prev") {
      setLightboxIndex((prev) => (prev === 0 ? items.length - 1 : prev - 1));
    } else {
      setLightboxIndex((prev) => (prev === items.length - 1 ? 0 : prev + 1));
    }
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") navigateLightbox("prev");
      if (e.key === "ArrowRight") navigateLightbox("next");
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, items.length]);

  if (!items || items.length === 0) return null;

  return (
    <>
      {/* Simple native scroll gallery - like Instagram/Threads */}
      <div
        ref={scrollRef}
        className={cn(
          "flex gap-3 overflow-x-auto scrollbar-hide my-10 py-2",
          "scroll-smooth snap-x snap-mandatory",
          className
        )}
        style={{
          WebkitOverflowScrolling: "touch",
        }}
      >
        {items.map((item, index) => (
          <div
            key={index}
            className="shrink-0 snap-start cursor-pointer flex flex-col"
            onClick={() => openLightbox(index)}
          >
            {item.type === "video" ? (
              <div 
                className="relative overflow-hidden"
                style={{ height: `${height}px`, borderRadius: "12px" }}
              >
                {item.poster ? (
                  <img
                    src={item.poster}
                    alt={item.alt || "Video thumbnail"}
                    className="h-full w-auto object-cover"
                    style={{ borderRadius: "12px" }}
                  />
                ) : (
                  <div 
                    className="h-full aspect-video bg-black/10 flex items-center justify-center"
                    style={{ borderRadius: "12px" }}
                  >
                    <Play className="w-12 h-12 text-black/50" />
                  </div>
                )}
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors rounded-xl">
                  <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
                    <Play className="w-6 h-6 text-black ml-1" />
                  </div>
                </div>
              </div>
            ) : (
              <img
                src={item.src}
                alt={item.alt || "Gallery image"}
                className="h-full w-auto object-cover"
                style={{ 
                  height: `${height}px`, 
                  borderRadius: "12px",
                }}
                loading="lazy"
              />
            )}
            {/* Caption below image */}
            {item.caption && (
              <p className="mt-2 text-xs text-black/50 max-w-[250px] leading-relaxed">
                {item.caption}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-50"
            aria-label="Close lightbox"
          >
            <X className="w-5 h-5 text-white" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              navigateLightbox("prev");
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-colors shadow-lg"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-6 h-6 text-black" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigateLightbox("next");
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-colors shadow-lg"
            aria-label="Next image"
          >
            <ChevronRight className="w-6 h-6 text-black" />
          </button>

          <div 
            className="max-w-[90vw] max-h-[90vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            {items[lightboxIndex].type === "video" ? (
              <video
                src={items[lightboxIndex].src}
                controls
                autoPlay
                className="max-w-full max-h-[80vh]"
                style={{ borderRadius: "12px" }}
              />
            ) : (
              <img
                src={items[lightboxIndex].src}
                alt={items[lightboxIndex].alt || "Gallery image"}
                className="max-w-full max-h-[80vh] object-contain"
                style={{ borderRadius: "12px" }}
              />
            )}
            {items[lightboxIndex].caption && (
              <p className="mt-4 text-white/80 text-center text-sm max-w-lg">
                {items[lightboxIndex].caption}
              </p>
            )}
            <p className="mt-2 text-white/50 text-sm">
              {lightboxIndex + 1} / {items.length}
            </p>
          </div>
        </div>
      )}
    </>
  );
}

// Simple wrapper for string arrays
interface SimpleGalleryProps {
  images: string[];
  height?: number;
  className?: string;
}

export function Gallery({ images, height, className }: SimpleGalleryProps) {
  const items: GalleryItem[] = images.map((src) => ({ src, type: "image" }));
  return <ImageGallery items={items} height={height} className={className} />;
}
